<template>
  <div :style="`height: ${100 - props.minusHeight}%`">
    <!-- <SceneDataSearch
      v-if="Object.keys(currentObj).length > 10"
      @search-criteria="(value) => (searchCriteria = value)" /> -->
    <TemplateNestingNav
      v-if="
        topLevelTemplate?.extra_info?.nested_template || breadcrumbs.length > 1
      "
      :topObj="topObj"
      :currentObj="currentObj"
      :templateUid="templateUid"
      :currentTemplate="currentTemplate"
      :breadcrumbs="breadcrumbs"
      :breadcrumbBack="breadcrumbBack"
      :jumpToBreadcrumb="jumpToBreadcrumb"
      :sceneDataResetFunc="sceneDataReset" />
    <SceneDataItems
      v-if="currentObj"
      @valueChanged="relayValueChanged($event)"
      style="overflow: hidden"
      :realtime_mode="realtime_mode"
      :currentObj="currentObj"
      :currentTemplate="currentTemplate"
      :searchCriteria="searchCriteria"
      :searchResults="searchResults"
      :originLevel="originLevel"
      :originObj="originObj"
      :resetFunc="resetValue"
      :breadcrumbs="breadcrumbs"
      :hide_activate_controls="hide_activate_controls"
      :hideicons="hide_type_icons"
      :createOrDeleteSceneData="createOrDeleteSceneData"
      :openSceneOptions="openSceneOptions"
      :safeTemplates="safeTemplates"
      :hide_pin_controls="hide_pin_controls"
      :disable_tabbing="disable_tabbing"
      :readOnlyMode="readOnlyMode" />
  </div>
</template>
<script setup>
// Imports
import { computed, onBeforeMount, onMounted, reactive, ref, watch } from 'vue';
import { deluge, state } from '@/stores/deluge.js';

// Components
import SceneDataItems from './SceneDataItems.vue';
import TemplateNestingNav from './SceneDataSubItems/TemplateNestingNav.vue';
import SceneDataSearch from './SceneDataSubItems/SceneDataSearch.vue';

import { nestedVersionCheck } from './utils';
import { composeSceneDataFromTemplate } from '@/utils/sceneDataComposition';
const props = defineProps([
  'originLevel',
  'originObj',
  'svars',
  'hideicons',
  'resetmsg',
  'realtime_mode',
  'templateUid',
  'safeTemplates',
  'hide_type_icons',
  'disable_tabbing',
  'hide_pin_controls',
  'hide_activate_controls',
  'readOnlyMode',
  'minusHeight',
]);

const emit = defineEmits(['update', 'valueChanged', 'breadcrumbsChanged']);

const topLevelTemplate = computed(() => {
  return deluge.templates[props.templateUid];
});

// TemplateNestingNav Top State for Controlling Object with Nav
// Breadcrumbs needs to live here as openSceneOptions, needs to track what depth we are at.
const currentTemplate = ref(deluge.templates[props.templateUid]);
const topObj = ref(props.svars); // Make topObj reactive so it updates when props.svars changes

const currentObj = ref(null);

const breadcrumbs = ref([]);
let immutableSvars = {};

watch(
  () => props.svars,
  () => {
    // Props have been changed into a new object:
    topObj.value = props.svars; // Update topObj when props change
    setCurrentObj(props.svars);
    breadcrumbs.value = [];
    breadcrumbs.value.push(createTopLevel(topObj.value, props.templateUid));
    emitBreadcrumbs();
  },

  { immediate: true }
);

function relayValueChanged(event) {
  // Adds context to the event before passing it to the parent.
  const [key, value] = event;

  const portLabel = breadcrumbs.value.at(-1).portLabel;
  const portMap = createPortMap();

  emit('valueChanged', {
    key: key,
    type: currentObj.value[key].type,
    data: value,
    portLabel: portLabel,
    portMap: portMap,
  });
}

function createPortMap() {
  const portMap = [];
  for (const breadcrumb of breadcrumbs.value)
    if (breadcrumb.portIndex !== undefined) portMap.push(breadcrumb.portIndex);
  return portMap;
}

function setCurrentObj(newObj) {
  // Clear the currentObj
  if (newObj.value) {
    // if refImpl
    newObj = newObj.value;
  }
  console.log(
    '[SceneData] setCurrentObj called, newObj keys:',
    Object.keys(newObj)
  );
  console.log(
    '[SceneData] setCurrentObj sample keys and values:',
    Object.keys(newObj)
      .slice(0, 3)
      .map((k) => ({
        key: k,
        type: newObj[k]?.type,
        value: newObj[k]?.value,
        label: newObj[k]?.label,
      }))
  );
  currentObj.value = newObj;
  if (props.originObj?.value) {
    props.originObj.value = newObj;
  }
  immutableSvars = JSON.parse(JSON.stringify(newObj));
}

function openSceneOptions(template, newObj, portLabel, portIndex) {
  // portLabel should be the actual key (e.g., "$Bottom Left"), not the display label
  console.log('[SceneData] openSceneOptions called:', {
    templateName: template.display_name,
    templateUid: template.uid,
    portLabel,
    portIndex,
    newObjKeys: newObj ? Object.keys(newObj) : 'null',
    newObjIsEmpty: newObj ? Object.keys(newObj).length === 0 : true,
    currentObjKeys: currentObj.value ? Object.keys(currentObj.value) : 'null',
    // Log details of first few keys to understand structure
    firstKeyDetails: newObj
      ? Object.keys(newObj)
          .slice(0, 3)
          .map((k) => ({
            key: k,
            type: newObj[k]?.type,
            hasValue: 'value' in (newObj[k] || {}),
            value: newObj[k]?.value,
            hasSceneData: 'scene_data' in (newObj[k] || {}),
          }))
      : [],
  });

  // Check if this is a nested template structure (has ports like $Top Left)
  // vs actual leaf data (has properties like .File, .Color, etc.)
  const hasOnlyPorts =
    newObj &&
    Object.keys(newObj).length > 0 &&
    Object.keys(newObj).every((k) => k.startsWith('$'));

  console.log('[SceneData] hasOnlyPorts:', hasOnlyPorts);
  console.log('[SceneData] Template structure:', {
    templateUid: template.uid,
    templateName: template.display_name,
    hasSceneData: !!deluge.templates[template.uid]?.scene_data,
    hasSrcSceneData: !!deluge.templates[template.uid]?.src_scene_data,
    sceneDataKeys: deluge.templates[template.uid]?.scene_data
      ? Object.keys(deluge.templates[template.uid].scene_data)
      : 'none',
    srcSceneDataKeys: deluge.templates[template.uid]?.src_scene_data
      ? Object.keys(deluge.templates[template.uid].src_scene_data)
      : 'none',
    isNestedTemplate:
      deluge.templates[template.uid]?.extra_info?.nested_template,
  });

  // If the object only contains port keys (nested template slots) but no actual
  // leaf properties, the scene_data was created but not fully populated.
  // This happens when a template is assigned to a port but createOrDeleteSceneData
  // only added the port structure. We need to fully compose the scene_data.
  if (hasOnlyPorts) {
    console.log(
      '[SceneData] Detected port-only structure, need to fully compose scene_data'
    );
    try {
      // Get the full composed scene data for this template
      const fullComposed = composeSceneDataFromTemplate(
        deluge.templates[template.uid],
        deluge.templates
      );
      console.log(
        '[SceneData] Full composed data keys:',
        Object.keys(fullComposed)
      );

      // Check if the composed data has non-port keys (leaf properties)
      const hasLeafProperties = Object.keys(fullComposed).some(
        (k) => !k.startsWith('$')
      );
      console.log(
        '[SceneData] Composed data has leaf properties:',
        hasLeafProperties
      );

      if (hasLeafProperties) {
        // Don't replace everything - just add missing leaf properties
        // Preserve existing port entries (which may already have scene_data populated)
        console.log(
          '[SceneData] Adding missing leaf properties to existing structure'
        );

        for (const [key, value] of Object.entries(fullComposed)) {
          // Only add keys that don't exist yet
          if (!(key in newObj)) {
            console.log('[SceneData] Adding missing key:', key);
            newObj[key] = value;
          } else {
            console.log(
              '[SceneData] Preserving existing key:',
              key,
              'with scene_data:',
              !!newObj[key]?.scene_data
            );
          }
        }
        console.log(
          '[SceneData] After merging, newObj keys:',
          Object.keys(newObj)
        );
      } else {
        console.log(
          '[SceneData] Template only has ports, no leaf properties to add'
        );
      }
    } catch (e) {
      console.warn('[SceneData] Failed to compose full scene data:', e);
    }
  } // Original empty object handling (for completely empty scene_data)
  try {
    if (
      newObj &&
      typeof newObj === 'object' &&
      Object.keys(newObj).length === 0
    ) {
      console.log(
        '[SceneData] Detected empty scene_data, populating with merged template data'
      );
      const merged = composeSceneDataFromTemplate(
        deluge.templates[template.uid],
        deluge.templates
      );
      console.log('[SceneData] Merged data keys:', Object.keys(merged));
      Object.assign(newObj, merged);
      console.log(
        '[SceneData] After Object.assign, newObj keys:',
        Object.keys(newObj)
      );
    }
  } catch (e) {
    console.warn('[SceneData] Failed to populate empty scene_data:', e);
  }

  breadcrumbs.value.push({
    name: template.display_name,
    portLabel: portLabel,
    portIndex: portIndex,
    object: newObj,
    uid: template.uid,
  });
  currentTemplate.value = deluge.templates[template.uid];
  console.log(
    '[SceneData] Setting currentTemplate to:',
    template.display_name,
    template.uid
  );
  setCurrentObj(newObj);
  emitBreadcrumbs();
}

function jumpToBreadcrumb(newObj) {
  let lastObj = breadcrumbs.value.at(-1).object;

  if (Object.is(newObj, lastObj)) {
    // If same object do nothing
    return;
  }

  if (Object.is(newObj, topObj.value)) {
    // If clicked top level, then just set it.
    setCurrentObj(newObj);
    currentTemplate.value = deluge.templates[props.templateUid];
    breadcrumbs.value = [createTopLevel(topObj.value, props.templateUid)];
    emitBreadcrumbs();
    return;
  }

  while (!Object.is(lastObj, newObj)) {
    breadcrumbs.value.pop();
    lastObj = breadcrumbs.value.at(-1).object;
  }
  setCurrentObj(newObj);
  const uid = breadcrumbs.value.at(-1).uid;
  currentTemplate.value = deluge.templates[uid];
  emitBreadcrumbs();
}

function breadcrumbBack() {
  if (Object.is(breadcrumbs.value.at(-1).object, topObj.value)) {
    return;
  }

  breadcrumbs.value.pop();
  const lastObj = breadcrumbs.value.at(-1);

  if (lastObj) {
    currentTemplate.value = deluge.templates[lastObj.uid];
    setCurrentObj(lastObj.object);
  }

  if (!lastObj) {
    currentTemplate.value = deluge.templates[props.templateUid];
    setCurrentObj(topObj.value);
  }
  emitBreadcrumbs();
}

function createTopLevel() {
  if (!props.templateUid) {
    return {};
  }
  const template = deluge.templates[props.templateUid];
  if (!template) {
    return {};
  }
  return {
    name: template.display_name,
    object: topObj.value,
    uid: template.uid,
  };
}

// Emit breadcrumb path to parent component
function emitBreadcrumbs() {
  const path = breadcrumbs.value
    .slice(1) // Skip the first item (top level)
    .map((b) => b.portLabel)
    .filter((label) => label !== undefined);
  console.log('[SceneData] Emitting breadcrumbs:', path);
  emit('breadcrumbsChanged', path);
}

// END

// Search Stuff
const searchCriteria = ref('');
const searchResults = ref({});

watch(searchCriteria, () => {
  updateSearchResults();
});

function updateSearchResults() {
  // Does this work for nested scenes?
  const tempVars = {};
  const sceneData = currentTemplate.value.scene_data;
  for (const key in sceneData) {
    if (
      sceneData[key].name
        .toLowerCase()
        .includes(searchCriteria.value.toLowerCase()) ||
      sceneData[key].label
        .toLowerCase()
        .includes(searchCriteria.value.toLowerCase())
    ) {
      tempVars[key] = sceneData[key];
    }
  }
  searchResults.value = tempVars;
}

// const safeTemplates =
//   props.safeTemplates === true
//     ? removeCircularAndSelf(props.templateUid)
//     : Object.values(deluge.templates);

//  All templates are safe? :pikachu-shocked-face:
// Templates yes, presets NOPE!
const safeTemplates = limitPresetOptions(props.templateUid);
// Removes Self and Circular References in Templates
// Needs more depth! Currently checks one layer.
function limitPresetOptions(uid) {
  if (deluge.templates[uid]?.extra_info?.preset) {
    return [];
  } else {
    return Object.values(deluge.templates);
  }
}
function removeCircularAndSelf(uid) {
  const safeTemplates = Object.values(deluge.templates).filter((template) => {
    if (template.uid == uid) {
      return false;
    }

    return isSceneDataSafe(template.scene_data, uid);
  });

  return safeTemplates;
}

function isSceneDataSafe(scene_data, uid) {
  for (const key in scene_data) {
    const thisObj = scene_data[key];

    if (thisObj.type === 'template' && thisObj.value === uid) {
      return false;
    }

    if (thisObj.scene_data) {
      return isSceneDataSafe(thisObj.scene_data, uid);
    }
  }

  return true;
}

// Unsure where this should live at the moment.
// This resets the scene_data, or creates it depending on if the value of the scene port etc is filled or not.
// I guess it's coupled with Exposed/ScenePorts/Layers
// We should look into refactoring these 3 to be similar!

function createOrDeleteSceneData(templateUidToAdd, item) {
  if (templateUidToAdd) {
    // create mode
    const template = deluge.templates[templateUidToAdd];
    item.value = templateUidToAdd;

    // Value not inherited, as we've just added it.
    item.keyInherited = false;
    item.scene_data = JSON.parse(JSON.stringify(template.src_scene_data));
    miniMerge(item, template);

    // Used for correct unMerge behavior with nested templates.
    // Until a user changes the subScene, it is considered inherited.
    for (const value of Object.values(item.scene_data)) {
      value.keyInherited = true;
    }
  } else {
    // delete mode
    delete item.scene_data;
    // remove value, copying Ventuz behavior
    item.value = null;
    item.keyInherited = false;
  }
}

// emulates composeSceneData on a smaller level, might be a better idea to just use that function? KB 10/05/2024
function miniMerge(item, template) {
  const template_data = JSON.parse(JSON.stringify(template.scene_data));
  for (const [key, value] of Object.entries(template_data)) {
    if (item.scene_data[key]) {
      // If the key exists in source
      const obj = item.scene_data[key];

      // DEFENSIVE: Check if obj is actually an object before setting properties
      if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
        console.warn(
          `Invalid scene data structure in miniMerge for key: ${key}. Expected object, got ${typeof obj}. Normalizing...`
        );
        // Normalize the data structure
        item.scene_data[key] = {
          value: obj,
          type: typeof obj,
          keyInherited: false,
        };
      }

      for (const [subKey, subValue] of Object.entries(value)) {
        if (subKey === 'scene_data') {
          // Lets not copy any nested scene_data.
          continue;
        }
        item.scene_data[key][subKey] = subValue;
      }
    }
  }
}

function sceneDataReset(currentTemplate, useSource) {
  let data_to_copy = currentTemplate.scene_data;

  if (useSource) {
    data_to_copy = currentTemplate.src_scene_data;
  }

  const obj = currentObj.value;

  for (const key in data_to_copy) {
    // DEFENSIVE: Check if obj[key] is actually an object before operating on it
    if (!obj[key] || typeof obj[key] !== 'object' || Array.isArray(obj[key])) {
      console.warn(
        `Invalid scene data structure in sceneDataReset for key: ${key}. Expected object, got ${typeof obj[key]}. Normalizing...`
      );
      // Normalize the data structure
      obj[key] = {
        value: obj[key],
        type: typeof obj[key],
        keyInherited: false,
      };
    }

    for (const subKey in obj[key]) delete obj[key][subKey];

    Object.assign(obj[key], data_to_copy[key]);

    const subTemplateUid = getSubTemplateUid(obj[key]);
    if (subTemplateUid) {
      createOrDeleteSceneData(subTemplateUid, obj[key]);
    }
  }
}

function getSubTemplateUid(sceneData) {
  if ('scene_data' in sceneData) return sceneData.value;
}

// This should probably live top level and we pass this as a function to each component via SceneData.

/**
 *
 * @param {string} key - the key to change
 * @param {string} originLevel  - 'scene' or 'template'
 */
function resetValue(key, originLevel) {
  let valueToUse = undefined;

  if (originLevel === 'scene' && currentTemplate.value.scene_data[key]) {
    // We're at the scene level, so a reset here should match template level. If template doesn't have it, then use SRC.
    valueToUse = currentTemplate.value.scene_data[key].value;
  } else {
    valueToUse = currentTemplate.value.src_scene_data[key].value;
  }

  const obj = currentObj.value;

  // DEFENSIVE: Check if obj[key] is actually an object before setting properties
  if (!obj[key] || typeof obj[key] !== 'object' || Array.isArray(obj[key])) {
    console.warn(
      `Invalid scene data structure for key: ${key}. Expected object, got ${typeof obj[key]}. Normalizing...`
    );
    // Normalize the data structure
    obj[key] = {
      value: obj[key] || valueToUse,
      type: typeof (obj[key] || valueToUse),
      keyInherited: false,
    };
  }

  // Updates the current merged data to display to the user what these changes will do.
  obj[key].value = valueToUse;

  // When this data is saved, this key and value is removed.
  obj[key].userIntent = 'remove';
}

// Method to navigate to a specific path programmatically
function navigateToPath(path) {
  console.log('[SceneData] navigateToPath called with:', path);
  if (!path || path.length === 0) {
    console.log('[SceneData] Empty path, returning');
    return;
  }

  // Start from the top
  let current = topObj.value;
  let currentTemplateUid = props.templateUid;

  console.log('[SceneData] Starting from topObj:', topObj.value);
  console.log('[SceneData] Template UID:', props.templateUid);

  // Clear breadcrumbs and reset to top
  breadcrumbs.value = [createTopLevel(topObj.value, props.templateUid)];

  // Navigate through each key in the path
  for (const portLabel of path) {
    console.log('[SceneData] Navigating to:', portLabel);
    console.log('[SceneData] Current object keys:', Object.keys(current));

    if (!current[portLabel]) {
      // Path is invalid, stop here
      console.warn(
        `[SceneData] Cannot navigate to ${portLabel}, path is invalid`
      );
      break;
    }

    const item = current[portLabel];
    console.log('[SceneData] Found item:', item);

    // Check if this is a nested scene with scene_data
    if (item.scene_data && item.value) {
      const template = deluge.templates[item.value];
      console.log('[SceneData] Template found:', template?.display_name);
      if (template) {
        // Navigate into this nested scene
        openSceneOptions(template, item.scene_data, portLabel, undefined);
        current = item.scene_data;
        currentTemplateUid = item.value;
        console.log('[SceneData] Navigated into:', portLabel);
      } else {
        // Template not found, can't navigate further
        console.warn(
          `[SceneData] Template ${item.value} not found for ${portLabel}`
        );
        break;
      }
    } else {
      // This item doesn't have nested scene_data, we're done
      console.log('[SceneData] Item has no nested scene_data, stopping');
      break;
    }
  }
  console.log(
    '[SceneData] Navigation complete. Final breadcrumbs:',
    breadcrumbs.value
  );
}

// Expose methods for parent component
defineExpose({
  navigateToPath,
});
</script>

<style scoped></style>
